import SideNavigation, { SideNavigationProps } from '@amzn/awsui-components-core/side-navigation';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logger } from 'src/logger';
import { useAppContext } from '../context/AppContextProvider';
import { useAuth } from '../context/AuthContextProvider';
import { UserActionsEventTypes } from '../das-finsuite/das-finsuite-constants';
import { ENVIRONMENT_VARIABLES } from '../../constants/EnvironmentConstants';
import { getFinSuiteNavigationItems, getOperationalExcellenceGroup, ROUTE_CONFIG } from '../../utilities/RouteConfig';
import { HomePageSideNavigationV2 } from '../side-navigation-v2/HomePageSideNavigationV2';

// Feature flag: Toggle between Cloudscape SideNavigation and custom-built V2 side navigation.
// Currently testing the V2 version with custom styling and embedded User Metrics widget.
// Set to `false` to revert to the original Cloudscape SideNavigation.
// Once V2 is finalized and approved, this flag and the original implementation below will be removed.
const USE_CUSTOM_SIDE_NAV = true;

const SupportLinksSectionGroup: SideNavigationProps.Item[] = [
  {
    type: 'divider'
  }
];

export const DFPHomePageSideNavigation: React.FC = () => {
  if (USE_CUSTOM_SIDE_NAV) {
    return <HomePageSideNavigationV2 />;
  }

  const userAuthenticationDetails = useAuth();
  const { homePageContent } = useAppContext();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const [sideNavigationItems, setSideNavigationItems] = useState<SideNavigationProps.Item[]>([]);
  const [activeRef, setActiveRef] = useState<string>('');

  useEffect(() => {
    setActiveRef(pathname + hash);
  }, [pathname, hash]);

  useEffect(() => {
    // Get base FinSuite navigation items from RouteConfig
    const baseFinSuiteItems = getFinSuiteNavigationItems(userAuthenticationDetails.userLDAPGroups, ENVIRONMENT_VARIABLES.env.Stage);
    const updatedItems = [...baseFinSuiteItems];

    // Operational Excellence section - now visible to all users (previously gated by isOEReadOnly)
    const operationalExcellenceGroup = getOperationalExcellenceGroup();
    updatedItems.push(operationalExcellenceGroup);

    // Add support links if enabled
    if (
      homePageContent?.sidePanelDetails?.supportLinks?.isEnabled &&
      homePageContent?.sidePanelDetails?.supportLinks?.tableContent &&
      homePageContent?.sidePanelDetails?.supportLinks?.tableContent?.length > 0
    ) {
      updatedItems.push(...SupportLinksSectionGroup);
      updatedItems.push(
        ...homePageContent.sidePanelDetails.supportLinks.tableContent
          .sort((a, b) => a.displayName.localeCompare(b.displayName))
          .map(
            (link) =>
              ({
                type: 'link',
                text: link.displayName,
                href: link.link,
                external: true
              } as SideNavigationProps.Item)
          )
      );
    }

    setSideNavigationItems(updatedItems);
  }, [userAuthenticationDetails, homePageContent?.sidePanelDetails]);

  const handleNavigation = (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
    if (!event.detail.external) {
      logger.info('DaS FinSuite -> User Action: FinSuite Home page side nav', {
        event_type: UserActionsEventTypes.HOME_PAGE_SIDE_NAV_CLICK,
        value: event.detail.text
      });
      event.preventDefault();
      navigate(event.detail.href, {
        state: {
          from: '/',
          redirectFromHomePage: true,
          fromAppName: '',
          fromAppId: ''
        }
      });
    }
  };

  return <SideNavigation items={sideNavigationItems} activeHref={activeRef} onFollow={handleNavigation} />;
};
